import { BarChart, TableChart } from '@mui/icons-material'
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { BarItem, BarChart as MuiBarChart } from '@mui/x-charts'
import { ClassificationReportResponse, ConfidenceInterval } from '@src/types/classificationReport'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import DocumentPagesDialog from './DocumentPagesDialog'
import ModelComparisonCell from './ModelComparisonCell'

interface ConfidenceIntervalsTableProps {
  reports: ClassificationReportResponse['reports']
}

const ConfidenceIntervalsTable = ({ reports }: ConfidenceIntervalsTableProps) => {
  const { t } = useTranslation()
  const [viewMode, setViewMode] = useState<'table' | 'chart'>('table')
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogPageIds, setDialogPageIds] = useState<string[]>([])
  const [dialogTitle, setDialogTitle] = useState('')

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'table' | 'chart' | null) => {
    if (newMode !== null) {
      setViewMode(newMode)
    }
  }

  const handleCellClick = (modelName: string, interval: string, resultType: 'TP' | 'FP' | 'FN', value: number) => {
    if (value === 0) return

    // Find the confidence interval for this model and interval
    const report = reports.find((r) => r.modelName === modelName)
    if (!report) return

    const confidenceInterval = report.confidenceIntervals.find((ci) => ci.interval === interval)
    if (!confidenceInterval) return

    const pageIds =
      resultType === 'TP'
        ? confidenceInterval.stats.truePositives.pageIds
        : resultType === 'FP'
          ? confidenceInterval.stats.falsePositives.pageIds
          : confidenceInterval.stats.falseNegatives.pageIds

    const resultTypeLabel = {
      TP: t('reports.truePositives'),
      FP: t('reports.falsePositives'),
      FN: t('reports.falseNegatives'),
    }[resultType]

    setDialogPageIds(pageIds)
    setDialogTitle(`${modelName} - ${interval} - ${resultTypeLabel} (${value})`)
    setDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setDialogOpen(false)
    setDialogPageIds([])
  }

  const handleCellClickWithModel = (
    modelName: string,
    interval: string,
    resultType: 'TP' | 'FP' | 'FN',
    value: number,
  ) => {
    handleCellClick(modelName, interval, resultType, value)
  }

  // Prepare chart data
  const chartData = reports[0].confidenceIntervals.map((interval, index) => {
    const dataPoint: Record<string, string | number> = {
      interval: interval.interval,
    }

    reports.forEach((report) => {
      const confidenceInterval = report.confidenceIntervals[index]
      dataPoint[`${report.modelName}_TP`] = confidenceInterval.stats.truePositives.value
      dataPoint[`${report.modelName}_FP`] = confidenceInterval.stats.falsePositives.value
      dataPoint[`${report.modelName}_FN`] = confidenceInterval.stats.falseNegatives.value
    })

    return dataPoint
  })

  // Generate series - each metric type has consistent color across models
  const chartSeries = reports.flatMap((report) => [
    {
      dataKey: `${report.modelName}_TP`,
      label: `${report.modelName} - TP`,
      stack: report.modelName,
      stackLabel: report.modelName,
      barLabel: (item: BarItem) => {
        const data = chartData[item.dataIndex]
        const fp = data[`${report.modelName}_FP`] as number
        const fn = data[`${report.modelName}_FN`] as number
        return fp > 0 || fn > 0 ? undefined : report.modelName
      },
      barLabelPlacement: 'outside' as const,
      color: '#4caf50',
    },
    {
      dataKey: `${report.modelName}_FP`,
      label: `${report.modelName} - FP`,
      stack: report.modelName,
      stackLabel: report.modelName,
      barLabel: (item: BarItem) => {
        const data = chartData[item.dataIndex]
        const fn = data[`${report.modelName}_FN`] as number
        return fn > 0 ? undefined : report.modelName
      },
      barLabelPlacement: 'outside' as const,
      color: '#f44336',
    },
    {
      dataKey: `${report.modelName}_FN`,
      label: `${report.modelName} - FN`,
      stack: report.modelName,
      stackLabel: report.modelName,
      barLabel: () => report.modelName,
      barLabelPlacement: 'outside' as const,
      color: '#ff9800',
    },
  ])

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="subtitle1">{t('reports.confidenceIntervals')}</Typography>
        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
          <ToggleButton value="table">
            <TableChart sx={{ mr: 0.5 }} fontSize="small" />
            Table
          </ToggleButton>
          <ToggleButton value="chart">
            <BarChart sx={{ mr: 0.5 }} fontSize="small" />
            Chart
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {viewMode === 'table' ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('reports.interval')}</TableCell>
                <TableCell align="center">{t('reports.truePositives')}</TableCell>
                <TableCell align="center">{t('reports.falsePositives')}</TableCell>
                <TableCell align="center">{t('reports.falseNegatives')}</TableCell>
                <TableCell align="center">{t('reports.truePositiveRate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports[0].confidenceIntervals.map((_, intervalIndex) => (
                <TableRow key={intervalIndex}>
                  <TableCell>{reports[0].confidenceIntervals[intervalIndex].interval}</TableCell>
                  <TableCell align="center">
                    <ModelComparisonCell
                      reports={reports}
                      intervalIndex={intervalIndex}
                      getValue={(interval: ConfidenceInterval) => interval.stats.truePositives.value}
                      getBestValue={(values: number[]) => Math.max(...values)}
                      resultType="TP"
                      onClick={handleCellClickWithModel}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ModelComparisonCell
                      reports={reports}
                      intervalIndex={intervalIndex}
                      getValue={(interval: ConfidenceInterval) => interval.stats.falsePositives.value}
                      getBestValue={(values: number[]) => Math.min(...values)}
                      resultType="FP"
                      onClick={handleCellClickWithModel}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ModelComparisonCell
                      reports={reports}
                      intervalIndex={intervalIndex}
                      getValue={(interval: ConfidenceInterval) => interval.stats.falseNegatives.value}
                      getBestValue={(values: number[]) => Math.min(...values)}
                      resultType="FN"
                      onClick={handleCellClickWithModel}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ModelComparisonCell
                      reports={reports}
                      intervalIndex={intervalIndex}
                      getValue={(interval: ConfidenceInterval) => interval.truePositivePercentage}
                      formatValue={(v: number) => `${v.toFixed(2)}%`}
                      getBestValue={(values: number[]) => Math.max(...values)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ width: '100%' }}>
          <MuiBarChart
            dataset={chartData}
            series={chartSeries}
            xAxis={[{ scaleType: 'band', dataKey: 'interval', label: 'Confidence Interval' }]}
            yAxis={[{ label: 'Count' }]}
            height={500}
          />
        </Box>
      )}

      {dialogPageIds.length > 0 && (
        <DocumentPagesDialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          pageIds={dialogPageIds}
          title={dialogTitle}
        />
      )}
    </>
  )
}

export default ConfidenceIntervalsTable
